import { useCallback, useEffect, useState } from "react";
import { Gift, Loader2, Send, CheckCircle2, Clock, Mail, Sparkles, Package } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { referralStatus, referralInvite, referralResend } from "@/lib/api";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Eingeladen", cls: "bg-muted text-muted-foreground", Icon: Mail },
  registered: { label: "Registriert", cls: "bg-blue-50 text-blue-700", Icon: Clock },
  qualified: { label: "Abo abgeschlossen", cls: "bg-primary/10 text-primary", Icon: CheckCircle2 },
};

export default function Referrals() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState("");
  const [dupInfo, setDupInfo] = useState(null);

  const load = useCallback(() => {
    referralStatus()
      .then(setData)
      .catch(() => {});
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const invite = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val) return;
    setBusy(true);
    try {
      await referralInvite(val);
      toast.success(`Einladung an ${val} gesendet.`);
      setEmail("");
      load();
    } catch (err) {
      const status = err?.response?.status;
      const d = err?.response?.data?.detail;
      if (status === 409) {
        setDupInfo(
          typeof d === "string"
            ? d
            : "Diese Person wurde bereits von einem anderen BeSca-Nutzer eingeladen."
        );
      } else {
        toast.error(typeof d === "string" ? d : "Einladung konnte nicht gesendet werden.");
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async (mail) => {
    setResending(mail);
    try {
      await referralResend(mail);
      toast.success(`Einladung an ${mail} erneut gesendet.`);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erneutes Senden fehlgeschlagen.");
    } finally {
      setResending("");
    }
  };

  if (!data) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="referrals-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!data.eligible) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="referrals-not-eligible">
        <Card className="p-8 text-center space-y-3">
          <Gift className="h-10 w-10 text-red-500 mx-auto" />
          <h1 className="text-xl font-medium">Weiterempfehlung</h1>
          <p className="text-sm text-muted-foreground">
            Der Weiterempfehlungsbonus steht ausschließlich Einzelnutzer-Konten zur Verfügung.
          </p>
        </Card>
      </div>
    );
  }

  const need = data.needed || 3;
  const done = Math.min(data.qualified_count || 0, need);
  const isLifetime = data.lifetime_free;

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="referrals-page">
      <div className="flex items-center gap-3">
        <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-red-50">
          <Gift className="h-6 w-6 text-red-500" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Weiterempfehlen &amp; LifeTime sichern</h1>
          <p className="text-sm text-muted-foreground">
            Werben Sie {need} Nutzer mit Jahresplan – und Ihr Einzelplatz wird dauerhaft gratis.
          </p>
        </div>
      </div>

      {isLifetime && (
        <Card
          className="p-5 border-primary/30 bg-primary/5 flex items-center gap-3"
          data-testid="referrals-lifetime-active"
        >
          <Sparkles className="h-6 w-6 text-primary shrink-0" />
          <div>
            <div className="font-medium text-primary">LifeTime aktiv 🎉</div>
            <p className="text-sm text-muted-foreground">
              Ihr BeSca-Einzelplatz ist dauerhaft kostenlos. Nicht in bar ablösbar.
            </p>
          </div>
        </Card>
      )}

      {/* Fortschritt */}
      <Card className="p-6 space-y-4" data-testid="referrals-progress">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Erfolgreiche Empfehlungen</span>
          <span className="text-sm text-muted-foreground" data-testid="referrals-progress-count">
            {done} / {need}
          </span>
        </div>
        <div className="flex gap-2">
          {Array.from({ length: need }).map((_, i) => (
            <div
              key={i}
              className={`h-2.5 flex-1 rounded-full transition-colors ${
                i < done ? "bg-primary" : "bg-muted"
              }`}
            />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          Eine Empfehlung zählt, sobald die eingeladene Person einen <strong>bezahlten
          Einzelnutzer-Jahresplan</strong> abgeschlossen hat.
        </p>
      </Card>

      {/* Einladen */}
      <Card className="p-6 space-y-4" data-testid="referrals-invite-card">
        <h2 className="text-base font-medium">Per E-Mail einladen</h2>
        <form onSubmit={invite} className="flex flex-col sm:flex-row gap-3">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@beispiel.de"
            className="bg-white"
            data-testid="referrals-email-input"
            required
          />
          <Button type="submit" disabled={busy} className="gap-2" data-testid="referrals-invite-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Einladung senden
          </Button>
        </form>
      </Card>

      {/* Einladungen */}
      <Card className="p-6 space-y-3" data-testid="referrals-list">
        <h2 className="text-base font-medium">Ihre Einladungen</h2>
        {data.invitations.length === 0 ? (
          <p className="text-sm text-muted-foreground">Noch keine Einladungen versendet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {data.invitations.map((inv, i) => {
              const meta = STATUS_META[inv.status] || STATUS_META.pending;
              const StIcon = meta.Icon;
              const subscribed = inv.status === "qualified";
              return (
                <li
                  key={i}
                  className="flex items-center justify-between gap-2 py-2.5"
                  data-testid="referrals-invitation-row"
                >
                  <span className="text-sm truncate flex-1">{inv.email}</span>
                  <div className="flex items-center gap-2 shrink-0">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${meta.cls}`}
                    >
                      <StIcon className="h-3.5 w-3.5" />
                      {meta.label}
                    </span>
                    {subscribed ? (
                      <span
                        title="Abo abgeschlossen"
                        data-testid="referrals-subscribed-icon"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-green-600"
                      >
                        <Package className="h-4 w-4" />
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => resend(inv.email)}
                        disabled={resending === inv.email}
                        title="Einladung erneut senden"
                        data-testid="referrals-resend-button"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-muted-foreground hover:text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                      >
                        {resending === inv.email ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Zwischenfenster: bereits von anderem Nutzer eingeladen */}
      <Dialog open={!!dupInfo} onOpenChange={(o) => !o && setDupInfo(null)}>
        <DialogContent data-testid="referrals-dup-dialog">
          <DialogHeader>
            <DialogTitle>Einladung nicht möglich</DialogTitle>
            <DialogDescription>{dupInfo}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button onClick={() => setDupInfo(null)} data-testid="referrals-dup-ok">
              Verstanden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
